import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Tescriptions from '@/components/Tescriptions';
import Table from '@/components/list/Table';
import StatusBadge from '@/components/order/StatusBadge';
import { ProductItemBox } from '@/components/product/ProductItem';
import { useUpdateOrderNote } from '@/hooks/mutations/admin';
import {
  useAdminProductOrder,
  useOrderNoteLogs,
  useOrderStatusLogs,
} from '@/hooks/queries/admin';
import usePagination from '@/hooks/usePagination';
import { getQueryKey } from '@/utils/queryKeys';

import CancelOrderModal from './components/CancelOrderModal';
import InvoiceModal from './components/InvoiceModal';
import UpdateOrderStatusModal from './components/UpdateOrderStatusModal';

const ProductOrderDetail = () => {
  const queryClient = useQueryClient();
  const params = useParams();
  const id = Number(params.id);

  const orderStatusLogsPagination = usePagination([], {
    useQueryParam: false,
  });
  const orderNoteLogsPagination = usePagination([], {
    useQueryParam: false,
  });

  const query = useAdminProductOrder(
    { id },
    {
      enabled: !!id,
    }
  );
  const getOrderStatusLogs = useOrderStatusLogs(
    {
      id,
      ...orderStatusLogsPagination.query,
    },
    { enabled: !!id }
  );
  const getOrderNoteLogs = useOrderNoteLogs(
    {
      id,
      ...orderNoteLogsPagination.query,
    },
    { enabled: !!id }
  );
  const updateOrderNote = useUpdateOrderNote({
    onSuccess: () => {
      setNote('');
      query.refetch();
      getOrderNoteLogs.refetch();
      message.success('내부용 메모가 수정되었어요');
    },
  });

  const [invoiceModal, setInvoiceModal] = useState(false);
  const [updateStatusModal, setUpdateStatusModal] = useState(false);
  const [cancelOrderModal, setCancelOrderModal] = useState(false);
  const [note, setNote] = useState('');

  if (!query.data) {
    return null;
  }

  return (
    <div css={tw`flex flex-col gap-[48px]`}>
      <Tescriptions title="주문자" column={2}>
        <Tescriptions.Item label="회원 이름">
          {query.data.user.name}
        </Tescriptions.Item>
        <Tescriptions.Item label="휴대폰 번호">
          {query.data.user.phone}
        </Tescriptions.Item>
        <Tescriptions.Item label="이메일">
          {query.data.user.email}
        </Tescriptions.Item>
        <Tescriptions.Item label="세금계산서 발행 여부">
          <div css={tw`flex flex-row items-center justify-between w-full`}>
            {query.data.isInvoiced ? '발행 완료' : '미발행'}
            <Button
              type="primary"
              disabled={query.data.isInvoiced}
              onClick={() => setInvoiceModal(true)}
            >
              발행하기
            </Button>

            <InvoiceModal
              onClose={() => setInvoiceModal(false)}
              onSuccess={() => {
                query.refetch();
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('admin:getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrder', {
                    id,
                  }),
                });
              }}
              isOpen={invoiceModal}
              orderId={id}
            />
          </div>
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="신청 지점" column={1}>
        <Tescriptions.Item label="지점명">
          {query.data.branch.branchName}
        </Tescriptions.Item>
      </Tescriptions>

      <ProductItemBox
        productId={query.data.id}
        title="신청 상품"
        url={query.data.bannerImageUrl}
        name={query.data.productName}
        summary={query.data.summary}
        price={query.data.price}
        discountPrice={query.data.discountPrice}
      />

      <Tescriptions title="추가 입력 문항" column={2}>
        <Tescriptions.Item label="관리자 문항">
          {query.data.additionalDescription}
        </Tescriptions.Item>
        <Tescriptions.Item label="파트너 답변">
          {query.data.additionalDescriptionRespond}
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="가격 정보" column={2}>
        <Tescriptions.Item label="정가">
          <Typography.Text css={tw`w-full text-right`}>
            {query.data.price.toLocaleString()}원
          </Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item label="원가">
          <Typography.Text css={tw`w-full text-right`}>
            {query.data.cost.toLocaleString()}원
          </Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item label="할인가">
          <Typography.Text css={tw`w-full text-right`}>
            {query.data.discountPrice.toLocaleString()}원
          </Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item
          label={
            <>
              수익금
              <span css={tw`font-normal text-[rgba(0, 0, 0, 0.45)]`}>
                (판매가-원가)
              </span>
            </>
          }
        >
          <Typography.Text css={tw`w-full text-right`}>
            {(
              query.data.price -
              query.data.discountPrice -
              query.data.cost
            ).toLocaleString()}
            원
          </Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item
          label={
            <>
              판매가
              <span css={tw`font-normal text-[rgba(0, 0, 0, 0.45)]`}>
                (정가-할인가)
              </span>
            </>
          }
        >
          <Typography.Text
            css={tw`w-full font-semibold text-right text-[#1677ff]`}
            color="primary"
          >
            {(query.data.price - query.data.discountPrice).toLocaleString()}원
          </Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item label="수익율">
          <Typography.Text css={tw`w-full text-right`}>
            {(
              ((query.data.price - query.data.discountPrice - query.data.cost) /
                (query.data.price - query.data.discountPrice)) *
              100
            )
              .toFixed(1)
              .toLocaleString()}
            %
          </Typography.Text>
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="신청 정보" column={1}>
        <Tescriptions.Item label="신청 상태">
          <div css={tw`flex flex-row items-center justify-between w-full`}>
            <StatusBadge status={query.data.status} />

            <div css={tw`flex flex-row gap-[8px]`}>
              <Button type="primary" onClick={() => setUpdateStatusModal(true)}>
                상태 변경
              </Button>

              <Button
                type="primary"
                danger
                onClick={() => setCancelOrderModal(true)}
                disabled={query.data.status === 'CANCELLED'}
              >
                신청 취소
              </Button>
            </div>

            <UpdateOrderStatusModal
              orderId={id}
              isOpen={updateStatusModal}
              onClose={() => setUpdateStatusModal(false)}
              onSuccess={() => {
                getOrderStatusLogs.refetch();
                query.refetch();
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('admin:getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrder', {
                    id,
                  }),
                });
              }}
            />

            <CancelOrderModal
              orderId={id}
              isOpen={cancelOrderModal}
              onClose={() => setCancelOrderModal(false)}
              onSuccess={() => {
                getOrderStatusLogs.refetch();
                query.refetch();
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('admin:getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrders'),
                });
                queryClient.invalidateQueries({
                  queryKey: getQueryKey('getProductOrder', {
                    id,
                  }),
                });
              }}
            />
          </div>
        </Tescriptions.Item>
        <Tescriptions.Item label="내부용 메모">
          <div css={tw`flex flex-row items-center gap-[12px] w-full`}>
            <Input.TextArea
              showCount
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <Button
              type="primary"
              disabled={query.data.note === note}
              onClick={() => {
                updateOrderNote.mutate({
                  id,
                  note,
                });
              }}
            >
              메모 저장
            </Button>
          </div>
        </Tescriptions.Item>
      </Tescriptions>

      <Table
        headerConfig={{
          title: '상태 변경 내역',
          showTotalCount: true,
          showSizeSelector: true,
        }}
        columns={[
          {
            dataIndex: 'id',
            title: 'No.',
            width: 70,
          },
          {
            dataIndex: 'statusAfter',
            title: '신청 상태',
            render: (_, row) => <StatusBadge status={row.statusAfter} />,
          },
          {
            dataIndex: 'userName',
            title: '변경자',
            width: 400,
          },
          {
            dataIndex: 'createdAt',
            title: '업데이트일',
            width: 400,
            render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
          },
        ]}
        dataSource={getOrderStatusLogs.data?.results}
        rowKey={(row) => row.id}
        pagination={{
          total: getOrderStatusLogs.data?.count,
          ...orderStatusLogsPagination,
        }}
      />

      <Table
        headerConfig={{
          title: '내부용 메모',
          showTotalCount: true,
          showSizeSelector: true,
        }}
        columns={[
          {
            dataIndex: 'id',
            title: 'No.',
            width: 70,
          },
          {
            dataIndex: 'note',
            title: '메모',
            render: (value) => (
              <Typography.Text css={tw`whitespace-pre`}>
                {value}
              </Typography.Text>
            ),
          },
          {
            dataIndex: 'ceatedAt',
            title: '업데이트일',
            width: 280,
            render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
          },
        ]}
        dataSource={getOrderNoteLogs.data?.results}
        rowKey={(row) => row.id}
        pagination={{
          total: getOrderNoteLogs.data?.count,
          ...orderNoteLogsPagination,
        }}
      />
    </div>
  );
};

ProductOrderDetail.titleExtra = () => {
  const navigate = useNavigate();

  return (
    <Button onClick={() => navigate(PATH.MARKETING_PRODUCT_MANAGE.ORDER)}>
      목록으로
    </Button>
  );
};

export default ProductOrderDetail;
